import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  external?: boolean;
  variant?: "default" | "mobile";
  highlight?: boolean;
}

const NavLink = ({ 
  to, 
  children, 
  className, 
  onClick, 
  external = false,
  variant = "default",
  highlight = false,
}: NavLinkProps) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  const baseStyles = {
    default: "group relative text-xs font-medium transition-all duration-300 whitespace-nowrap",
    mobile: "text-sm font-medium transition-colors hover:text-primary"
  };

  const activeStyles = {
    default: highlight
      ? "text-secondary"
      : (isActive ? "text-primary" : "text-muted-foreground hover:text-primary"),
    mobile: highlight
      ? "text-secondary font-semibold"
      : (isActive ? "text-primary" : "text-muted-foreground")
  };

  const combinedClassName = cn(
    baseStyles[variant],
    activeStyles[variant],
    className
  );

  const innerSpan = highlight
    ? "relative z-10 px-2.5 py-1 rounded-full border border-secondary/40 bg-secondary/10 text-secondary font-semibold transition-all duration-300 group-hover:bg-secondary group-hover:text-primary group-hover:border-secondary"
    : "relative z-10 px-1.5 py-0.5 rounded-md transition-all duration-300 group-hover:bg-primary/10";

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={combinedClassName}
        onClick={onClick}
      >
        {variant === "default" ? (
          <span className={innerSpan}>
            {children}
          </span>
        ) : (
          children
        )}
      </a>
    );
  }

  return (
    <Link
      to={to}
      className={combinedClassName}
      onClick={onClick}
    >
      {variant === "default" ? (
        <span className={innerSpan}>
          {children}
        </span>
      ) : (
        children
      )}
    </Link>
  );
};

export { NavLink };
